import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, failed
  const [paymentData, setPaymentData] = useState(null);

  const verifyPayment = useCallback(async (token) => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/payment/iyzico/callback?token=${token}`;
      console.log('🌐 Making API call to:', apiUrl);
      console.log('Method: POST');
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      const result = await response.json();
      console.log('📦 Response data:', result);

      if (result.success) {
        console.log('✅ Payment verified successfully');
        setStatus('success');
        setPaymentData(result);
        toast.success('Ödeme başarıyla tamamlandı!');
        
        setTimeout(() => {
          navigate(result.redirect_url || '/siparislerim');
        }, 3000);
      } else {
        console.error('❌ Payment verification failed:', result.error);
        setStatus('failed');
        toast.error('Ödeme başarısız: ' + result.error);
        
        setTimeout(() => {
          navigate('/sepetim');
        }, 3000);
      }
    } catch (error) {
      console.error('='.repeat(80));
      console.error('❌ PAYMENT VERIFICATION ERROR');
      console.error('Error:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('='.repeat(80));
      setStatus('failed');
      toast.error('Ödeme doğrulama hatası');
      
      setTimeout(() => {
        navigate('/sepetim');
      }, 3000);
    }
  }, [navigate]);

  useEffect(() => {
    console.log('='.repeat(80));
    console.log('🔍 PaymentCallbackPage mounted');
    console.log('URL:', window.location.href);
    console.log('Search params:', Object.fromEntries(searchParams));
    
    const token = searchParams.get('token');
    console.log('Token from URL:', token);
    
    if (!token) {
      console.error('❌ No token found in URL');
      setStatus('failed');
      toast.error('Ödeme token\'ı bulunamadı');
      return;
    }

    console.log('✅ Token found, starting verification...');
    verifyPayment(token);
  }, [searchParams, verifyPayment]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {status === 'processing' && (
          <div className="bg-white rounded-xl p-12 text-center">
            <Loader2 className="w-24 h-24 mx-auto text-trendyol-orange mb-6 animate-spin" />
            <h2 className="font-manrope font-bold text-2xl mb-4">Ödeme Doğrulanıyor</h2>
            <p className="text-gray-600">Lütfen bekleyin, ödemeniz kontrol ediliyor...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-xl p-12 text-center">
            <CheckCircle className="w-24 h-24 mx-auto text-green-600 mb-6" />
            <h2 className="font-manrope font-bold text-2xl mb-4 text-green-600">Ödeme Başarılı!</h2>
            <p className="text-gray-600 mb-6">
              Ödemeniz başarıyla tamamlandı. Siparişiniz hazırlanıyor.
            </p>
            {paymentData && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ödeme ID:</span>
                    <span className="font-medium">{paymentData.payment_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sipariş ID:</span>
                    <span className="font-medium">{paymentData.order_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tutar:</span>
                    <span className="font-medium">{paymentData.amount} TL</span>
                  </div>
                  {paymentData.card_info && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Kart:</span>
                        <span className="font-medium">
                          {paymentData.card_info.type} - ****{paymentData.card_info.last_four}
                        </span>
                      </div>
                      {paymentData.card_info.installment > 1 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Taksit:</span>
                          <span className="font-medium">{paymentData.card_info.installment} Taksit</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            <p className="text-sm text-gray-500">Siparişlerim sayfasına yönlendiriliyorsunuz...</p>
          </div>
        )}

        {status === 'failed' && (
          <div className="bg-white rounded-xl p-12 text-center">
            <XCircle className="w-24 h-24 mx-auto text-red-600 mb-6" />
            <h2 className="font-manrope font-bold text-2xl mb-4 text-red-600">Ödeme Başarısız</h2>
            <p className="text-gray-600 mb-6">
              Ödemeniz tamamlanamadı. Lütfen tekrar deneyin.
            </p>
            <p className="text-sm text-gray-500">Sepetinize yönlendiriliyorsunuz...</p>
          </div>
        )}
      </div>
    </div>
  );
}
